import type { Expense, Project, Quote, QuoteItem } from "../types/domain";

export interface QuoteTotals {
  subtotal: number;
  displacement: number;
  discount: number;
  total: number;
  cost: number;
  profit: number;
  margin: number;
}
const safeMoney = (value: number) =>
  Number.isFinite(value) && value > 0 ? value : 0;
export const calculateQuoteItemTotal = (item: QuoteItem) =>
  safeMoney(item.quantity) * safeMoney(item.unitPrice);
export function calculateQuoteTotals(
  quote: Pick<Quote, "items" | "displacement" | "discount">,
): QuoteTotals {
  const subtotal = quote.items.reduce(
    (sum, item) => sum + calculateQuoteItemTotal(item),
    0,
  );
  const cost = quote.items.reduce(
    (sum, item) => sum + safeMoney(item.quantity) * safeMoney(item.unitCost),
    0,
  );
  const displacement = safeMoney(quote.displacement);
  const discount = safeMoney(quote.discount);
  const total = Math.max(0, subtotal + displacement - discount);
  const profit = total - cost;
  return {
    subtotal,
    displacement,
    discount,
    total,
    cost,
    profit,
    margin: total > 0 ? (profit / total) * 100 : 0,
  };
}
export const calculateProjectCosts = (projectId: string, expenses: Expense[]) =>
  expenses
    .filter((expense) => expense.projectId === projectId)
    .reduce((sum, expense) => sum + expense.value, 0);
export function calculateProjectMargin(project: Project, expenses: Expense[]) {
  const costs = calculateProjectCosts(project.id, expenses);
  const margin = project.contractValue - costs;
  return {
    costs,
    margin,
    marginPercent: project.contractValue
      ? (margin / project.contractValue) * 100
      : 0,
  };
}
export type ProjectHealth = "Saudável" | "Atenção" | "Risco";
export function calculateProjectHealth(
  physicalProgress: number,
  financialProgress: number,
): { delta: number; status: ProjectHealth } {
  const delta = physicalProgress - financialProgress;
  return {
    delta,
    status: delta >= -5 ? "Saudável" : delta >= -15 ? "Atenção" : "Risco",
  };
}
export const calculatePhysicalProgress = (project: Project) =>
  project.physicalProgress;
export function calculateMeasurementTotal(input: {
  width: number;
  length: number;
  height: number;
  quantity: number;
  unit: "m" | "m²" | "m³";
}): number {
  const { width, length, height, quantity, unit } = input;
  if (
    [width, length, height, quantity].some(
      (value) => !Number.isFinite(value) || value < 0,
    )
  )
    return 0;
  if (unit === "m") return width * quantity;
  if (unit === "m²") return width * height * quantity;
  return width * length * height * quantity;
}
export const calculateFinancialProgress = (
  project: Project,
  expenses: Expense[],
) =>
  project.contractValue
    ? (calculateProjectCosts(project.id, expenses) / project.contractValue) *
      100
    : 0;
